import React, { useRef } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import AddIcon from "@mui/icons-material/Add";
import { MuiChipsInput } from "mui-chips-input";
import * as Yup from "yup";
import Modal from "Components/Common/Modal";
import { usePutUpdateAlternateEditFieldsMutation } from "apis/ApplicationRequestDetail";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";

const AlternateEmailEditField = ({
  t,
  applicationData: { applicantId, alternateEmail = [] },
  handleClose,
}) => {
  const theme = useTheme();

  const ref = useRef();
  const StyledTextField = styled(MuiChipsInput)`
    label {
      width: "100%";
    }
    .MuiInputBase-root {
      padding: 5px 10px 0 0;
    }
  `;

  const [updateAlternateEmail, { isLoading }] =
    usePutUpdateAlternateEditFieldsMutation();

  const formik = useFormik({
    initialValues: {
      alternateEmail: alternateEmail || [],
    },
    validationSchema: Yup.object({
      alternateEmail: Yup.array().test(
        "isValidAlternateEmail",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];

          if (
            !!current.alternateEmail.length &&
            !current.alternateEmail.every((email) =>
              email?.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              )
            )
          ) {
            return this.createError({
              message: "Enter valid emails",
            });
          }
          if (current.alternateEmail.some((email) => email === current.email)) {
            return this.createError({
              message: "Alternate email can't be same as email.",
            });
          }
          return true;
        }
      ),
    }),
    onSubmit: async ({ alternateEmail }) => {
      const payload = {
        id: applicantId,
        alternateEmail: alternateEmail.join(", ") || null,
      };
      try {
        await updateAlternateEmail({
          payload,
          toastMessage: "Alternate email updated successfully",
        });
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: { alternateEmail: emails },
    errors,
    setFieldValue,
    handleSubmit,
  } = formik;

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Edit Alternate Email"
      maxWidth="sm"
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12}>
          <StyledTextField
            className="w-100"
            inputRef={ref}
            value={emails}
            size="small"
            label={t("Alternate Email")}
            placeholder={t("Enter Alternate Email")}
            onChange={(value) => setFieldValue("alternateEmail", value)}
            clearInputOnBlur
            InputProps={{
              endAdornment: (
                <Tooltip title="Add Email">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      if (ref.current?.value) {
                        setFieldValue("alternateEmail", [
                          ...emails,
                          ref.current.value,
                        ]);
                        ref.current.value = "";
                      }
                    }}
                    edge="end"
                    color="primary"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
          <Typography
            variant="subtitle3"
            className={`${
              errors.alternateEmail ? "visible" : "invisible"
            } input-field-error-message`}
            sx={{ color: theme.palette.error.main }}
          >
            {t(errors.alternateEmail)}
          </Typography>
        </Grid>

        <Grid item xs={12} className="d-flex justify-content-end">
          <Button
            variant="outlined"
            size="small"
            sx={{ height: "40px" }}
            disabled={isLoading}
            onClick={handleSubmit}
          >
            Save {isLoading && <CircularProgress size={20} className="ms-2" />}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

AlternateEmailEditField.propTypes = {
  t: PropTypes.func,
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
};

export default withTranslation()(AlternateEmailEditField);
